import { extendTheme } from "@chakra-ui/react";
import type { StyleFunctionProps } from "@chakra-ui/theme-tools";
import { mode } from "@chakra-ui/theme-tools";

import { colors } from "./theme";

const theme = extendTheme({
  colors: {
    ...colors,
    text: {
      primary: "#1A1A1A",
      secondary: "#4A4A4A",
    },
  },
  fonts: {
    heading: "BelfastGrotesk",
    body: "BelfastGrotesk",
  },
  components: {
    Heading: {
      baseStyle: (props: StyleFunctionProps) => ({
        color: mode("text.primary", "text.primary")(props),
        fontSize: "4xl",
      }),
    },
    Input: {
      parts: ["field"],
      baseStyle: (props: StyleFunctionProps) => ({
        field: {
          color: mode("text.primary", "text.primary")(props),
          bg: mode("white", "background.mid")(props),
          borderColor: mode("gray.200", "neutrals.border")(props),
          _placeholder: {
            color: mode("text.secondary", "text.secondary")(props),
            opacity: 0.8,
          },
          _hover: {
            borderColor: mode("gray.300", "neutrals.four")(props),
          },
          _focus: {
            borderColor: mode("brand.500", "brand.500")(props),
            boxShadow: mode(
              "0 0 0 1px var(--chakra-colors-brand-500)",
              "0 0 0 1px var(--chakra-colors-brand-500)"
            )(props),
          },
        },
      }),
      variants: {
        search: (props: StyleFunctionProps) => ({
          field: {
            color: mode("gray.800", "white")(props),
            bg: mode("white", "#1A1A1A")(props),
            border: "none",
            borderRadius: "10px",
            height: "50px",
            boxShadow: mode(
              "0 0 1px 1px var(--chakra-colors-brand-500), 0 2px 12px rgba(136, 120, 251, 0.2)",
              "inset 2px 2px 5px rgba(0, 0, 0, 0.2), inset -2px -2px 5px rgba(255, 255, 255, 0.1)"
            )(props),
            _placeholder: {
              color: mode("gray.500", "neutrals.four")(props),
            },
            _focus: {
              boxShadow: mode(
                "0 0 1px 1px var(--chakra-colors-brand-500), 0 2px 16px rgba(136, 120, 251, 0.3)",
                "inset 3px 3px 7px rgba(0, 0, 0, 0.3), inset -2px -2px 5px rgba(255, 255, 255, 0.1)"
              )(props),
            },
          },
        }),
      },
    },
    Checkbox: {
      borderRadius: "20px",
      baseStyle: {
        control: {
          borderRadius: "full",
          borderColor: "neutrals.four",
        },
      },
    },
    Menu: {
      parts: ["button"],
      baseStyle: {
        button: {
          _active: { background: "transparent" },
          _expanded: { background: "transparent" },
          _focus: { boxShadow: "outline" },
          _hover: { background: "transparent" },
        },
      },
    },
    Slider: {
      parts: ["container", "track", "thumb", "filledTrack"],
      baseStyle: {
        track: {
          background: "brand.500",
        },
        thumb: {
          background: "brand.500",
        },
        filledTrack: { background: "brand.500" },
      },
    },
    Spinner: {
      baseStyle: {
        color: "brand.500",
      },
      defaultProps: {
        size: "xl",
        thickness: "4px",
      },
    },
    Table: {
      parts: ["table", "td", "th", "tr"],
      defaultProps: { colorScheme: "brand" },
      baseStyle: {
        table: {
          background: "transparent",
        },
        thead: {
          th: {
            color: "black",
            borderBottomColor: "brand.500",
          },
        },
        tbody: {
          td: {
            borderBottomColor: "brand.500",
          },
          tr: {
            background: "transparent",
            _hover: { background: "inherited" },
          },
        },
      },
      variants: {
        noHover: {
          tbody: {
            tr: {
              _hover: { background: "inherit" },
            },
          },
        },
      },
    },
    Button: {
      defaultProps: { colorScheme: "brand" },
      baseStyle: {
        borderRadius: "20px",
        _focus: {
          outline: "none",
          boxShadow:
            "6px 6px 7px var(--chakra-colors-brand-500_6), -6px -6px 7px var(--chakra-colors-brand-500_3)",
        },
        _hover: {
          _disabled: {
            backgroundColor: "neutrals.two",
            transform: "none",
          },
          transition: "all 0.1s ease-in-out",
          transform: "scale(0.98)",
        },
        _disabled: {
          backgroundColor: "neutrals.two",
          color: "neutrals.three",
          opacity: 1,
        },
      },
      variants: {
        secondary: {
          backgroundColor: "none",
          border: "2px solid var(--chakra-colors-brand-500)",
          color: "brand.500",
          _disabled: {
            borderWidth: 0,
          },
        },
      },
    },
    Text: {
      baseStyle: (props: StyleFunctionProps) => ({
        color: mode("text.primary", "text.primary")(props),
      }),
    },
  },
  styles: {
    global: {
      "*, *:before, *:after": {
        borderStyle: "solid",
        borderColor: "brand.500",
      },
      "button, a, [role='button'], [tabindex]": {
        WebkitTapHighlightColor: "transparent",
        WebkitTouchCallout: "none",
        WebkitUserSelect: "none",
        userSelect: "none",
      },
      a: {
        color: "brand.500",
        textDecoration: "underline",
        _hover: { opacity: "0.8" },
      },
      body: {
        bgGradient:
          "linear(to-b, background.gradient.start, background.gradient.mid, background.gradient.finish)",
        height: "100vh",
        width: "100vw",
      },
    },
  },
  semanticTokens: {
    colors: {
      // Semantic color tokens for components
      "bg.main": {
        default: "background.main",
        _dark: "background.dark",
      },
      "bg.panel": {
        default: "background.components.panel",
        _dark: "background.dark",
      },
      "bg.player": {
        default: "background.components.player",
        _dark: "background.dark",
      },
      "bg.footer": {
        default: "background.components.footer",
        _dark: "background.dark",
      },
      "bg.entry": {
        default: "background.components.entry.inactive",
        _dark: "background.dark",
      },
      "bg.entry.active": {
        default: "background.components.entry.active",
        _dark: "background.components.entry.active",
      },
    },
  },
});

export const externalLinksTokens = {
  container: {
    px: 6,
    contentSpacing: 2,
    headerSpacing: 4,
  },
  icon: {
    size: 6,
  },
} as const;

export default theme;
